import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { SetPassword } from 'components/admin';
import { Button, FlexContainer, MoreOptions } from 'components/common';
import { useBoxContext, useBoxDispatch } from 'components/common/box';
import { DropdownDivider } from 'components/common/dropdown';
import { StatCount } from 'components/common/stats';
import {
  UsersCreate,
  UsersDeleteInBatch,
  UsersExportExcel,
  UsersImportCsv,
  UsersLockInBatch,
  UsersSendMessage,
  UsersUpdateInBatch
} from '.';

import type { EmailTemplateData } from 'shared-types';
import type { UpdateUserObject } from './Users';

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
  updateUsers: (updateUser: UpdateUserObject) => Promise<void>;
}

const UsersListHeader = ({
  emailTemplates,
  emailTemplatesDefault,
  updateUsers
}: Props) => {
  const { selectedItems } = useBoxContext();

  const boxDispatch = useBoxDispatch();

  const {
    messages,
    pageState: { loading, usersCount },
    serverConfig: { accountEnableUsernameAccounts, useAlternativeSetPassword },
    user
  } = useReduxState();

  const [count, setCount] = useState(0);

  useEffect(() => {
    if (loading) return;
    setCount(usersCount);
  }, [loading, usersCount]);

  const selectedUserIds = Object.keys(selectedItems);

  return (
    <div className="header">
      <div className="users">
        <div className="count">
          <StatCount
            isLarge={true}
            label={messages.usersFiltersUsers}
            value={count}
          />
        </div>

        {selectedUserIds && selectedUserIds.length ? (
          <>
            <div className="count selected">
              <StatCount
                isLarge={true}
                label={messages.usersFiltersUsersSelected}
                value={selectedUserIds.length}
              />
            </div>

            <Button
              category="light"
              icon="times"
              onClick={() => boxDispatch({ type: 'resetSelection' })}
              size="small"
              text={messages.commonClear}
            />
          </>
        ) : null}
      </div>

      <div className="actions">
        <div className="send">
          <UsersSendMessage
            id="users-list-header-send-message"
            buttonSize="header"
          />
        </div>

        {(user?.roles?.includes('superadmin') ||
          (user?.roles?.includes('admin') &&
            user?.permissions?.includes('view-all-users'))) &&
        emailTemplatesDefault ? (
          <>
            <FlexContainer>
              <UsersCreate
                emailTemplates={emailTemplates}
                emailTemplatesDefault={emailTemplatesDefault}
                updateUsers={updateUsers}
              />

              <UsersImportCsv
                emailTemplates={emailTemplates}
                emailTemplatesDefault={emailTemplatesDefault}
                updateUsers={updateUsers}
              />
            </FlexContainer>
          </>
        ) : null}

        <MoreOptions category="light" styleContentButtons={false}>
          <UsersExportExcel />

          {user?.roles.includes('superadmin') ||
          user?.permissions.includes('manage-users') ? (
            <>
              <DropdownDivider />

              {accountEnableUsernameAccounts ? (
                <SetPassword
                  buttonIsDropdown={true}
                  useAlternative={useAlternativeSetPassword}
                />
              ) : null}

              <UsersLockInBatch handleSuccess={updateUsers} />

              <UsersUpdateInBatch handleSuccess={updateUsers} />

              <UsersDeleteInBatch handleSuccess={updateUsers} />
            </>
          ) : null}
        </MoreOptions>
      </div>

      <style jsx>{`
        .header {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .users {
          flex-grow: 1;
          min-width: 260px;
        }
        .count {
          display: inline-block;
          margin-right: 10px;
          padding-bottom: 3px;
        }
        .selected {
          border: 1px solid #999;
          border-width: 0 0 0 1px;
          padding-left: 10px;
        }
        .actions {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
      `}</style>
    </div>
  );
};

export default UsersListHeader;
